import { useRouter } from "@uirouter/react";
import { noop } from "lodash";
import { useCallback, useContext, useMemo } from "react";
import { useDispatch } from "react-redux";
import { filter, shareReplay, take } from "rxjs/operators";
import styled from "styled-components";
import { PrimaryButton } from "../button";
import { FileField } from "../forms/file-3";
import { Select } from "../forms/select";
import { useForm } from "../hooks/form/form";
import { Icon } from "../icons";
import { columnFlex } from "../layout";
import { isProgress, Progress, ResultWithProgress } from "../loader/models";
import { GlobalOverlayContext } from "../overlay/context";
import { LoaderActions } from "../../store/loader/actions";
import { VerificationDTO } from "../../store/verification/models/dto/kyc";
import {
  FileCategory,
  FileType,
} from "../../store/verification/models/internal";
import { VerificationService } from "../../store/verification/service";
import { mapObject } from "../../utils/utils";
import { Container } from "./common";

const UploadIcon = styled(Icon).attrs({ type: "upload" })`
  display: block;
  width: 50%;
  height: 50%;
  transition: height 0.2s ease-in-out;
  transition: width 0.2s ease-in-out;

  &:hover {
    width: 60%;
    height: 60%;
  }
`;

const Wrap = styled.form`
  ${columnFlex}
  flex-direction: column;
  min-width: 20em;
  align-items: stretch;
  > *:not(:last-child) {
    margin-bottom: 1em;
  }
`;

function AddImageModal({
  kycId,
  options,
  defaults,
}: {
  kycId: string;
  options: {
    type: FileType;
    category: FileCategory;
  };
  defaults: {
    type: FileType["T"];
    category: FileCategory["T"];
  };
}) {
  const {
    handleChange,
    values,
    errors,
    register,
    handleSubmit,
  } = useForm<VerificationDTO.Requests.UploadKycDocument.T>({
    defaultValues: useMemo(
      () => ({
        category: defaults.category,
        type: defaults.type,
        file: null,
      }),
      [defaults]
    ),
  });

  const categoryOptions = useMemo(
    () =>
      mapObject(options.category.enum, (val, key) => ({
        title: val,
        value: key,
      })),
    []
  );

  const typeOptions = useMemo(
    () =>
      mapObject(options.type.enum, (val, key) => ({
        title: val,
        value: key,
      })),
    []
  );

  const dispatch = useDispatch();
  const router = useRouter();

  const onSubmit = useCallback(
    (data: VerificationDTO.Requests.UploadKycDocument.T) => {
      const post$ = VerificationService.uploadKycDocument(kycId, data).pipe(
        shareReplay(1)
      );

      post$
        .pipe(filter<ResultWithProgress<void>, Progress>(isProgress), take(1))
        .subscribe((event) => {
          dispatch(LoaderActions.setFileLoader(event.data));
        });

      post$.subscribe(
        noop,
        () => dispatch(LoaderActions.clearLoader()),
        () => {
          dispatch(LoaderActions.clearLoader());
          router.stateService.reload();
        }
      );

      return post$.toPromise().catch(noop);
    },
    [dispatch, kycId, router]
  );

  return (
    <Wrap onSubmit={handleSubmit(onSubmit)}>
      <Select
        {...register("category")}
        fullWidth
        options={categoryOptions}
        disabled={categoryOptions.length < 2}
      />
      <Select
        {...register("type")}
        fullWidth
        options={typeOptions}
        disabled={typeOptions.length < 2}
      />
      <FileField
        onChange={handleChange("file")}
        value={values["file"]}
        error={!!errors["file"]}
        label="Upload Image"
        helperText={errors["file"]?.message}
      />
      <PrimaryButton type="submit">Submit</PrimaryButton>
    </Wrap>
  );
}

export function AddImage({
  kycId,
  options,
  defaults,
}: {
  kycId: string;
  options: {
    type: FileType;
    category: FileCategory;
  };
  defaults: {
    type: FileType["T"];
    category: FileCategory["T"];
  };
}) {
  const { setOverlay } = useContext(GlobalOverlayContext);

  return (
    <Container
      onClick={() =>
        setOverlay({
          title: "Add Image",
          children: (
            <AddImageModal
              kycId={kycId}
              options={options}
              defaults={defaults}
            />
          ),
        })
      }
    >
      <UploadIcon colour1="#ccc" />
      Upload Image
    </Container>
  );
}
